/**
 * Branded sign-in: the web mirror of apps/api/src/auth/sign-in/sign-in-methods.ts (3 Nov 2026; contract
 * docs/superpowers/specs/2026-11-03-branded-sign-in-design.md section 1). Keys, audiences and defaults ONLY: the
 * resolver lives in the API, and the web never decides what is allowed, it draws what the API answered.
 *
 * Frozen after wave 0 (coordinator). Builder 9 owns lib/sign-in/__tests__/methods-parity.spec.ts, which reads the API
 * file and fails when the two lists drift.
 */

export const SIGN_IN_METHOD_KEYS = [
  'password',
  'email_code',
  'passkey',
  'magic_link',
  'hf_microsoft',
  'hf_google',
  'org_microsoft',
  'org_google',
  'org_saml',
  'org_oidc',
] as const
export type SignInMethodKey = (typeof SIGN_IN_METHOD_KEYS)[number]

export const ORG_METHOD_KEYS = ['org_microsoft', 'org_google', 'org_saml', 'org_oidc'] as const
export type OrgMethodKey = (typeof ORG_METHOD_KEYS)[number]

export const HF_APP_METHOD_KEYS = ['hf_microsoft', 'hf_google'] as const
export type HfAppMethodKey = (typeof HF_APP_METHOD_KEYS)[number]

/**
 * The order a sign-in page draws the methods in (the API's `offered` comes in it): the forms, then the company's OWN
 * apps, then HumanFirewall's shared Microsoft and Google (evidence run X4a, 4 Nov 2026). The admin page and C1's
 * preview both follow it, so the preview never shows an order the page does not draw.
 */
export const SIGN_IN_PAGE_ORDER = [
  'password',
  'email_code',
  'passkey',
  'magic_link',
  'org_microsoft',
  'org_google',
  'org_saml',
  'org_oidc',
  'hf_microsoft',
  'hf_google',
] as const satisfies readonly SignInMethodKey[]

/** `keys` in SIGN_IN_PAGE_ORDER (a stable sort: a key the list does not know keeps its place at the end). */
export function inSignInPageOrder<K extends string>(keys: readonly K[]): K[] {
  const at = (key: string) => {
    const index = (SIGN_IN_PAGE_ORDER as readonly string[]).indexOf(key)
    return index < 0 ? SIGN_IN_PAGE_ORDER.length : index
  }
  return [...keys].sort((a, b) => at(a) - at(b))
}

export const SIGN_IN_POLICY_AUDIENCES = ['admins', 'employees'] as const
export type SignInPolicyAudience = (typeof SIGN_IN_POLICY_AUDIENCES)[number]

export const SIGN_IN_AUDIENCE_KEYS = ['admins', 'employees', 'phishtrainer'] as const
export type SignInAudienceKey = (typeof SIGN_IN_AUDIENCE_KEYS)[number]

/** The admin switches, in C1's order (no magic_link row: email_code governs the admin magic link). */
export const ADMIN_METHOD_KEYS = [
  'password',
  'email_code',
  'passkey',
  'hf_microsoft',
  'hf_google',
  'org_microsoft',
  'org_google',
  'org_saml',
  'org_oidc',
] as const satisfies readonly SignInMethodKey[]
export type AdminMethodKey = (typeof ADMIN_METHOD_KEYS)[number]

/** The employee switches, in C2's order. */
export const EMPLOYEE_METHOD_KEYS = [
  'email_code',
  'magic_link',
  'org_microsoft',
  'org_google',
  'org_saml',
  'org_oidc',
] as const satisfies readonly SignInMethodKey[]
export type EmployeeMethodKey = (typeof EMPLOYEE_METHOD_KEYS)[number]

/** What kind of host a page is served on. */
export type SignInHostKind = 'platform' | 'platform_subdomain' | 'customer_cname'

/** The provider family of an org method: picks the button's logo (Microsoft, Google, building for SAML / SSO). */
export const ORG_METHOD_PROVIDER: Readonly<Record<OrgMethodKey, 'microsoft' | 'google' | 'saml' | 'oidc'>> = {
  org_microsoft: 'microsoft',
  org_google: 'google',
  org_saml: 'saml',
  org_oidc: 'oidc',
}

export function isOrgMethod(key: string): key is OrgMethodKey {
  return (ORG_METHOD_KEYS as readonly string[]).includes(key)
}

/** A `next` path a sign-in may return to: relative, one leading slash, no scheme, no backslash. */
export function isSafeNextPath(next: string | null | undefined): next is string {
  return typeof next === 'string' && /^\/(?![/\\])[^\s\\]*$/.test(next)
}
